import { useState, useRef, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { categories } from "@/mocks/categories";

export default function Navbar() {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const handleClickOutside = useCallback((e: MouseEvent) => {
    if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
      setDropdownOpen(false);
    }
  }, []);

  useEffect(() => {
    if (dropdownOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [dropdownOpen, handleClickOutside]);

  const handleCategoryClick = (url: string) => {
    setDropdownOpen(false);
    setMobileMenuOpen(false);
    window.open(url, "_blank", "noopener,noreferrer");
  };

  const closeMobile = () => setMobileMenuOpen(false);

  return (
    <nav className="fixed top-0 left-0 w-full z-50 bg-background-50/95 backdrop-blur-md border-b border-background-200/70 transition-all duration-300">
      <div className="max-w-7xl mx-auto px-4 md:px-6 flex items-center justify-between h-16">
        <Link
          to="/"
          className="flex items-center gap-2 group"
          onClick={closeMobile}
        >
          <span className="text-lg font-bold text-foreground-900 tracking-tight font-display">
            allchinabuyspreadsheet<span className="text-accent-500">.sbs</span>
          </span>
        </Link>

        <div className="hidden md:flex items-center gap-4">
          <Link
            to="/"
            className="text-sm font-medium text-foreground-600 hover:text-accent-600 transition-colors duration-200 whitespace-nowrap"
          >
            Home
          </Link>

          <div ref={dropdownRef} className="relative">
            <button
              onClick={() => setDropdownOpen(!dropdownOpen)}
              className="flex items-center gap-1.5 px-4 py-2 rounded-full border border-foreground-200/60 text-sm font-medium text-foreground-700 hover:border-accent-400 hover:text-accent-600 transition-colors duration-200 whitespace-nowrap cursor-pointer bg-background-50/80"
            >
              <span>Categories</span>
              <i className={`ri-arrow-down-s-line text-base transition-transform duration-200 ${dropdownOpen ? "rotate-180" : ""}`}></i>
            </button>

            {dropdownOpen && (
              <div className="absolute top-full mt-2 left-0 w-72 bg-background-50 border border-background-200/80 rounded-xl shadow-lg overflow-hidden max-h-[420px] overflow-y-auto">
                {categories.map((cat) => (
                  <button
                    key={cat.name}
                    onClick={() => handleCategoryClick(cat.url)}
                    className="w-full flex items-center gap-3 px-4 py-3 text-left text-sm text-foreground-700 hover:bg-accent-50 hover:text-accent-700 transition-colors duration-150 whitespace-nowrap cursor-pointer"
                  >
                    <span className="w-8 h-8 flex items-center justify-center rounded-lg bg-accent-100 text-accent-600 flex-shrink-0">
                      <i className={`${cat.icon} text-base`}></i>
                    </span>
                    <span dangerouslySetInnerHTML={{ __html: cat.name }}></span>
                    <i className="ri-external-link-line text-xs text-foreground-300 ml-auto"></i>
                  </button>
                ))}
              </div>
            )}
          </div>

          <Link
            to="/best-allchinabuy-spreadsheet-guide"
            className="text-sm font-medium text-foreground-600 hover:text-accent-600 transition-colors duration-200 whitespace-nowrap"
          >
            Shopping Guide
          </Link>

          <a
            href="https://kaspreadsheet.com"
            target="_blank"
            rel="noopener nofollow"
            className="px-5 py-2 rounded-full bg-primary-500 text-white text-sm font-semibold hover:bg-primary-600 transition-colors duration-200 whitespace-nowrap cursor-pointer"
          >
            AllChinaBuy Sheet
          </a>
        </div>

        <button
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          className="md:hidden w-10 h-10 flex items-center justify-center rounded-lg text-foreground-700 cursor-pointer hover:bg-background-100 transition-colors duration-200"
          aria-label="Toggle menu"
        >
          <i className={`text-xl ${mobileMenuOpen ? "ri-close-line" : "ri-menu-line"}`}></i>
        </button>
      </div>

      {mobileMenuOpen && (
        <div className="md:hidden bg-background-50 border-b border-background-200/70 px-4 py-4 space-y-1 max-h-[70vh] overflow-y-auto">
          <Link
            to="/"
            onClick={closeMobile}
            className="w-full flex items-center gap-3 px-3 py-3 text-left text-sm font-semibold text-foreground-800 hover:bg-accent-50 rounded-lg transition-colors duration-150"
          >
            <span className="w-8 h-8 flex items-center justify-center rounded-md bg-foreground-900 text-white flex-shrink-0">
              <i className="ri-home-4-line text-sm"></i>
            </span>
            Home
          </Link>

          <Link
            to="/best-allchinabuy-spreadsheet-guide"
            onClick={closeMobile}
            className="w-full flex items-center gap-3 px-3 py-3 text-left text-sm font-semibold text-foreground-800 hover:bg-accent-50 rounded-lg transition-colors duration-150"
          >
            <span className="w-8 h-8 flex items-center justify-center rounded-md bg-accent-500 text-white flex-shrink-0">
              <i className="ri-book-open-line text-sm"></i>
            </span>
            Shopping Guide
          </Link>

          <div className="border-t border-background-200/60 my-2 pt-2">
            <div className="text-xs text-foreground-400 font-medium px-3 py-2 uppercase tracking-wider">
              Product Categories
            </div>
          </div>

          {categories.map((cat) => (
            <button
              key={cat.name}
              onClick={() => handleCategoryClick(cat.url)}
              className="w-full flex items-center gap-3 px-3 py-2.5 text-left text-sm text-foreground-700 hover:bg-accent-50 rounded-lg transition-colors duration-150 whitespace-nowrap cursor-pointer"
            >
              <span className="w-8 h-8 flex items-center justify-center rounded-md bg-accent-100 text-accent-600 flex-shrink-0">
                <i className={`${cat.icon} text-sm`}></i>
              </span>
              <span dangerouslySetInnerHTML={{ __html: cat.name }}></span>
              <i className="ri-external-link-line text-xs text-foreground-300 ml-auto"></i>
            </button>
          ))}

          <div className="border-t border-background-200/60 mt-2 pt-3">
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              onClick={closeMobile}
              className="w-full flex items-center gap-3 px-3 py-3 text-left text-sm font-semibold text-white bg-foreground-900 hover:bg-foreground-800 rounded-lg transition-colors duration-150 mt-1"
            >
              <span className="w-8 h-8 flex items-center justify-center rounded-md bg-white/20 flex-shrink-0">
                <i className="ri-external-link-line text-sm"></i>
              </span>
              AllChinaBuy Sheet
            </a>
          </div>
        </div>
      )}
    </nav>
  );
}